import {
  CheckOutlined,
  CopyOutlined,
  DeleteTwoTone,
  EditTwoTone,
  LockOutlined,
  ReloadOutlined,
  LoadingOutlined,
  PlusOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import {
  Spin,
  Row,
  Button,
  Col,
  Input,
  Table,
  Tooltip,
  Select,
  Modal,
  message,
  Tag,
  Space,
} from "antd";
import { ColumnsType } from "antd/es/table";
import React from "react";
import { Link } from "react-router-dom";
import UserService, {
  roleOption,
  supportRoleOption,
  roleTableShow,
  userDetailsProps,
} from "../../Services/UserService";
import { useUser } from "../../Context/UserContext";
import { adminContext } from "../../Common/PageRoute";
import AddUser from "./AddUser";
import {
  PageLayout,
  PageHeader,
  SectionCard,
  TableToolbar,
} from "../../Common/Layout";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import {
  ApplicationUserStatus,
  ApplicationUserStatusOptions,
  IPagingPagingItems,
} from "../../Common/CommonComponent";
import Checkbox from "antd/es/checkbox/Checkbox";
import { EditUserPanel } from "./EditUserPanel";

interface ListDataType {
  key: React.Key;
  id: number;
  fullName: string;
  phoneNumber: string;
  email: string;
  createdBy: any;
  updatedBy: any;
  status: ApplicationUserStatus;
  roles: string[];
  practiceId: number;
}

interface ListData {
  items: ListDataType[];
  totalItems: number;
}
const AdminUsers = () => {
  const { isSuperAdmin, isSubAdmin, isSupportAdmin } = useUser();
  const isSupportAdminOnly =
    isSupportAdmin() && !isSuperAdmin() && !isSubAdmin();
  const [loading, setLoading] = React.useState(false);
  const [dialog, setDialog] = React.useState<{ type: string; params: any }>({
    type: "",
    params: {},
  });
  const [pagingItems, setPagingItems] = React.useState<IPagingPagingItems>({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 15,
  });
  const [listParams, setListParams] = React.useState<{
    refresh: boolean;
    search: string;
    sortDir: string;
    sortCol: string;
    action: ApplicationUserStatus;
    roles: string;
  }>({
    refresh: true,
    search: "",
    sortDir: "ascend",
    sortCol: "FullName",
    action: ApplicationUserStatus.Active,
    roles: "",
  });

  const setListParamsAndRefresh = (key: any, value: any) => {
    setPagingItems({ ...pagingItems, currentPage: 1, pageSize: 15 });
    setListParams((p: any) => ({ ...p, [key]: value }));
  };

  const primaryActions: any[] = [
    {
      label: "User",
      icon: <PlusOutlined />,
      type: "primary",
      onClick: () => setDialog({ type: "userdailog", params: {} }),
    },
  ];

  return (
    <PageLayout>
      <PageHeader title="Admin Users" subtitle="Manage user accounts" />
      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            primaryActions={primaryActions}
            onSearch={(val: string) => setListParamsAndRefresh("search", val)}
            searchPlaceholder="Search users..."
            extra={
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Select
                  placeholder="Select Role"
                  defaultValue={""}
                  style={{ minWidth: 140 }}
                  onChange={(val: string) =>
                    setListParamsAndRefresh("roles", val)
                  }
                  options={
                    isSuperAdmin()
                      ? [{ value: "", label: "All Roles" }, ...roleOption]
                      : [
                          { value: "", label: "All Support Roles" },
                          ...supportRoleOption,
                        ]
                  }
                />
                <Select
                  placeholder="Select Status"
                  style={{ minWidth: 140 }}
                  defaultValue={ApplicationUserStatus.Active}
                  onChange={(val: ApplicationUserStatus) =>
                    setListParamsAndRefresh("action", val)
                  }
                  options={ApplicationUserStatusOptions?.map((x: any) => ({
                    value: x.value,
                    label: x.label,
                  }))}
                />
              </div>
            }
          />
        </div>
        <Spin
          spinning={loading}
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        >
          <UserDetails
            practiceId={0}
            setLoading={setLoading}
            listParams={listParams}
            setListParams={(v: any) => {
              setListParams(v);
            }}
            pagingItems={pagingItems}
            setPagingItems={(v: IPagingPagingItems) => {
              setPagingItems(v);
            }}
          />
        </Spin>
      </SectionCard>

      {dialog.type === "userdailog" && (
        <AddUser
          id={dialog?.params?.id}
          onDismiss={(confirmation: any) => {
            if (confirmation) {
              setListParams({ ...listParams, refresh: !listParams.refresh });
            }
            setDialog({ type: "", params: {} });
          }}
        />
      )}
    </PageLayout>
  );
};

export default AdminUsers;

export const UserDetails = ({
  practiceId,
  setLoading,
  listParams,
  setListParams,
  pagingItems,
  setPagingItems,
}: userDetailsProps) => {
  const { isSuperAdmin, isSubAdmin, isSupportAdmin } = useUser();
  const isSupportAdminOnly =
    isSupportAdmin() && !isSuperAdmin() && !isSubAdmin();
  const { openNotification } = React.useContext(adminContext);
  const [usersList, setUsersList] = React.useState<ListData>({
    items: [],
    totalItems: 0,
  });

  const [copied, setCopied] = React.useState(false);
  const [trigger, setTrigger] = React.useState(false);
  const [editUserId, setEditUserId] = React.useState<string | null>(null);
  const [dialog, setDialog] = React.useState<{ type: string; params: any }>({
    type: "",
    params: {},
  });

  const onClickToggleUserStatus = (record: any) => {
    setDialog({
      type: "isActive",
      params: {
        name: record.fullName,
        id: record.id,
        status: record?.status,
      },
    });
  };

  const onClickResetPassword = (record: any) => {
    setDialog({
      type: "resetPasswordConfirm",
      params: {
        id: record.id,
        name: record.fullName,
        email: record.email,
      },
    });
  };

  const onTablePageChange = (
    paging: { current?: number; pageSize?: number },
    filter: Record<string, any>,
    sort: { order?: string; columnKey?: string },
  ) => {
    setListParams &&
      setListParams((prev: any) => {
        return {
          ...prev,
          sortDir: sort?.order ? (sort.order as string) : "ascend",
          sortCol: sort?.order ? String(sort.columnKey) : "FullName",
        };
      });
    if (paging?.current && paging?.pageSize) {
      setPagingItems((prev: any) => {
        return {
          ...prev,
          currentPage: paging?.current,
          pageSize: paging?.pageSize,
        };
      });
    }
  };

  const columns: ColumnsType<ListDataType> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: "5%",
      render: (_: any, record: any, index: any) => (
        <span>
          {pagingItems.currentPage > 1
            ? pagingItems.pageSize * (pagingItems.currentPage - 1) + index + 1
            : index + 1}
        </span>
      ),
    },
    {
      title: "Name",
      dataIndex: "fullName",
      key: "FullName",
      sorter: true,
      defaultSortOrder: "ascend",
      width: "18%",
      render: (text: any, record: any) => (
        <Link
          title={text}
          to={`/adminuser/profile/${record.id}`}
          state={{ userName: record.fullName }}
        >
          {record.fullName}
        </Link>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "Email",
      sorter: true,
      width: "20%",
    },
    {
      title: "Phone number",
      dataIndex: "phoneNumber",
      key: "PhoneNumber",
      sorter: true,
      width: "15%",
    },
    // {
    //   title: "Practice",
    //   dataIndex: "practiceId",
    //   key: " practiceId",
    //   width: "15%",
    //   render: (text) => <label>{`Practice ${text}`}</label>,
    // },

    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      width: "15%",
      render: (text: any, record: any) => {
        const allowedKeys = [
          "SUPERADMIN",
          "SUBADMIN",
          "SUPPORTUSER",
          "SUPPORTMANAGER",
          "SUPPORTADMIN",
          "SuperAdmin",
          "SubAdmin",
          "SupportUser",
          "SupportManager",
          "SupportAdmin",
        ];
        const uniqueRoles: string[] = [
          ...new Set<string>(record?.roles ?? []),
        ].filter((r: string) =>
          allowedKeys.some((k: string) => k.toLowerCase() === r.toLowerCase()),
        );
        if (uniqueRoles.length === 0) return "-";
        return (
          <Space size={4} wrap>
            {uniqueRoles.map((item: any, idx: any) => {
              const upperKey = item.toUpperCase();
              const normKey =
                upperKey === "SUPERADMINC"
                  ? "SUPERADMIN"
                  : upperKey === "SUBADMINC"
                    ? "SUBADMIN"
                    : upperKey;
              const label =
                roleTableShow[normKey] || roleTableShow[item] || item;
              const color =
                normKey === "SUPERADMIN"
                  ? "magenta"
                  : normKey === "SUBADMIN"
                    ? "blue"
                    : "cyan";
              return (
                <Tag
                  key={idx}
                  color={color}
                  style={{ borderRadius: 4, margin: 0 }}
                >
                  {label}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "Id",
      render: (text: any, record: any) => (
        // <div style={{ display: "flex", justifyContent: "space-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* // <div style={{ display: "flex", gap: "10px" }}> */}
          <div style={{ width: 24, textAlign: "center" }}>
            {record.status === ApplicationUserStatus.Active && (
              <Tooltip
                title={"Edit User"}
                placement="top"
                color="white"
                overlayInnerStyle={{ color: "#000" }}
              >
                <span
                  className="edit-btn"
                  onClick={() => setEditUserId(String(record.id))}
                  style={{
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <EditTwoTone />
                </span>
              </Tooltip>
            )}
          </div>

          <div style={{ width: 24, textAlign: "center" }}>
            {record.status === ApplicationUserStatus.Active && (
              <Tooltip
                title={"Reset Password"}
                placement="top"
                color="white"
                overlayInnerStyle={{ color: "#000" }}
              >
                <ReloadOutlined
                  style={{ color: "#faad14", cursor: "pointer", fontSize: 16 }}
                  onClick={() => onClickResetPassword(record)}
                />
              </Tooltip>
            )}
          </div>

          <div style={{ width: 24, textAlign: "center" }}>
            {/* <Tooltip
              title={
                record?.status === ApplicationUserStatus.Active
                  ? "In-Active"
                  : "Active"
              }
              placement="top"
              color="white"
              overlayInnerStyle={{ color: "#000" }}
            >
              {record.status === ApplicationUserStatus.Inactive ? (
                <RollbackOutlined twoToneColor="#52c41a" onClick={(e: any) => onClickToggleUserStatus(record)} />
              ) : (
                <DeleteTwoTone twoToneColor="#ff4d4f" onClick={(e: any) => onClickToggleUserStatus(record)}
                // setSelectChange(record?.isActive ? "In-Active" : "Active");
                />
              )}
            </Tooltip> */}

            <Checkbox
              checked={record.status === ApplicationUserStatus.Active}
              onChange={(e: any) => {
                onClickToggleUserStatus(record);
              }}
            />
          </div>

          {/* <FolderViewOutlined/> */}
        </div>
      ),
    },
  ];

  const getItems = async (start: any, length: any) => {
    let res = await UserService.getUsers(
      start,
      length,
      listParams?.search ?? null,
      listParams?.sortCol ?? null,
      listParams?.sortDir ?? null,
      practiceId,
      listParams?.action ?? ApplicationUserStatus.Active,
      listParams?.roles ?? "",
    );
    if (res?.status) {
      if (setLoading) setLoading(false);
      var rawItems = res?.result?.items || [];
      var validItems = rawItems.filter((item: any) => item && item.id);
      if (isSupportAdminOnly) {
        const supportRolesList = [
          "SUPPORTUSER",
          "SUPPORTMANAGER",
          "SUPPORTADMIN",
          "SupportUser",
          "SupportManager",
          "SupportAdmin",
        ];
        validItems = validItems.filter(
          (item: any) =>
            item.roles?.some((r: string) => supportRolesList.includes(r)) ||
            supportRolesList.includes(item.role),
        );
      }
      var data: ListDataType[] = validItems.map((item: any) => {
        var itemWithKey = {
          key: item?.id,
          id: item?.id,
          fullName: item?.fullName,
          roles: item?.roles,
          phoneNumber: item?.phoneNumber,
          email: item?.email,
          practiceId: item?.practiceId,
          createdBy: item?.createdBy,
          status: item?.status,
        };
        return itemWithKey;
      });
      setUsersList({
        totalItems: res?.result?.totalRecords,
        items: data,
      });
    } else {
      openNotification("error", res.message);
      if (setLoading) setLoading(false);
    }
  };

  React.useEffect(() => {
    if (setLoading) setLoading(true);
    getItems(
      (pagingItems.currentPage - 1) * pagingItems.pageSize,
      pagingItems.pageSize,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParams, trigger]);

  return (
    <>
      <Row>
        <Col span={24}>
          <Table
            rowKey="id"
            dataSource={usersList?.items}
            columns={columns}
            size="small"
            // className="aoa-custom-table"
            className="border-top-table"
            locale={{
              triggerDesc: "",
              triggerAsc: "",
              cancelSort: "",
            }}
            scroll={{ x: 800, y: window.innerHeight - 256 }}
            pagination={{
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              current: pagingItems.currentPage,
              pageSize: pagingItems.pageSize,
              showSizeChanger: true,
              total: usersList?.totalItems,
              pageSizeOptions: ["1", "15", "25", "50", "100"],
            }}
            onChange={(paging, filter, sort: any) =>
              onTablePageChange(paging, filter, sort)
            }
          />
        </Col>
      </Row>
      {dialog.type === "isActive" && (
        <ConfirmationModal
          open={true}
          onNo={() => setDialog({ type: "", params: "" })}
          onYes={async () => {
            try {
              const apiData = await UserService.toggleUserStatus(
                dialog.params?.id,
                dialog.params?.status === ApplicationUserStatus.Inactive
                  ? ApplicationUserStatus.Active
                  : ApplicationUserStatus.Inactive,
              );
              if (apiData.status) {
                openNotification(
                  "success",
                  `User "${dialog?.params?.name}" ${
                    dialog?.params?.status === ApplicationUserStatus.Inactive
                      ? "Active"
                      : "InActive"
                  } sucessfully.`,
                );

                setTrigger((x) => !x);
                setDialog({ type: "", params: "" });
              } else {
                openNotification("error", apiData?.message);
                console.log("error", apiData?.message);
                setDialog({ type: "", params: "" });
              }
            } catch (err) {
              openNotification("error", err);
              console.log("error", err);
              setDialog({ type: "", params: "" });
            } finally {
              setDialog({ type: "", params: "" });
            }
          }}
          text={`Are you sure you want to ${
            dialog?.params?.status === ApplicationUserStatus.Inactive
              ? "Active"
              : "In-Active"
          }  this "${dialog?.params?.name}" user. ?`}
        />
      )}

      {dialog.type === "resetPasswordConfirm" && (
        <ConfirmationModal
          open={true}
          onNo={() => setDialog({ type: "", params: "" })}
          onYes={async () => {
            try {
              const apiData = await UserService.resetPassword(
                dialog.params?.id,
              );
              if (apiData.status) {
                setDialog({
                  type: "showResetPasswordSuccess",
                  params: {
                    name: dialog.params?.name,
                    email: dialog.params?.email,
                    password: apiData.result,
                  },
                });
              } else {
                openNotification(
                  "error",
                  apiData?.message || "Failed to reset password",
                );
                setDialog({ type: "", params: "" });
              }
            } catch (err: any) {
              openNotification(
                "error",
                err?.message || "Error resetting password",
              );
              setDialog({ type: "", params: "" });
            } finally {
              if (setLoading) setLoading(false);
            }
          }}
          text={`Are you sure you want to reset password for "${dialog?.params?.name}"?`}
        />
      )}

      {dialog.type === "showResetPasswordSuccess" && (
        <Modal
          open={true}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <LockOutlined style={{ color: "#52c41a", fontSize: 20 }} />
              <span>Password Reset Successful</span>
            </div>
          }
          onCancel={() => setDialog({ type: "", params: "" })}
          footer={[
            <Button
              key="done"
              type="primary"
              onClick={() => setDialog({ type: "", params: "" })}
            >
              Done
            </Button>,
          ]}
        >
          <div style={{ padding: "16px 0" }}>
            <p style={{ marginBottom: 12, color: "#374151" }}>
              The password for <strong>{dialog.params?.name}</strong> (
              {dialog.params?.email}) has been reset successfully.
            </p>
            <div style={{ marginBottom: 6, fontWeight: 600, color: "#4B5563" }}>
              New Password:
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Input.Password
                value={dialog.params?.password}
                readOnly
                style={{ fontFamily: "monospace", fontSize: 15 }}
              />
              <Button
                icon={
                  copied ? (
                    <CheckOutlined style={{ color: "#52c41a" }} />
                  ) : (
                    <CopyOutlined />
                  )
                }
                onClick={() => {
                  navigator.clipboard.writeText(dialog.params?.password);
                  setCopied(true);
                  message.success("Password copied to clipboard!");
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <EditUserPanel
        open={!!editUserId}
        userId={editUserId}
        onClose={() => setEditUserId(null)}
        onSuccess={() => setTrigger((x) => !x)}
      />
    </>
  );
};
